// @flow
import React, { useContext } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Grid } from 'semantic-ui-react';

import {
  CLUSTER_PROVIDER_BACKEND_NAME,
  CONNECTOR_AVAILABLE_NETWORKING_TYPES_LIST_BACKEND_NAME,
  CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME,
  CONNECTOR_DESTINATION_PROVIDER_DISPLAY_NAME,
  CONNECTOR_DESTINATION_REGION_BACKEND_NAME,
  CONNECTOR_DESTINATION_REGION_DISPLAY_NAME,
  CONNECTOR_SMT_TYPE_BACKEND_NAME,
  IS_CONNECT_CROSS_CLOUD_REGION_LIVE,
} from '../../../constants';
import { CLUSTER_DETAILS_CONFIG_MAP } from '../../../configuration/cluster-details';
import { ClusterContext } from '../../../contexts/ClusterContext';
import { useRegionOptionsWithGovCloudDisabledIfRequired } from '../../../custom-hooks/useRegionOptionsWithGovCloudDisabledIfRequired';
import {
  getClusterProviderDropdownOptions,
  getRegionDropdownOptions,
} from '../cluster-details-inputs/utils';

import { ConnectorMetadataDropdownContainer } from './ConnectorMetadataDropdownContainer';
import {
  getConnectorNetworkingOptions,
  getConnectorSmtTypeOptions,
} from './ConnectorsMonthlyInputContainer';
import { getDestinationDropdownsVisibility } from './utils';

export const ConnectorMetadataInputsContainer = ({ connectorPath }) => {
  const { values, setFieldValue } = useFormikContext();
  const cluster = useContext(ClusterContext);
  const connectorNameToRecordsMap = cluster?.cluster_configs?.connectorNameToRecordsMap;

  const networkingTypeFieldName = `${connectorPath}.connectorNetworkingType`;
  const destinationProviderFieldName = `${connectorPath}.${CONNECTOR_DESTINATION_PROVIDER_BACKEND_NAME}`;
  const destinationRegionFieldName = `${connectorPath}.${CONNECTOR_DESTINATION_REGION_BACKEND_NAME}`;

  const connectorNameValue = getIn(values, `${connectorPath}.connectorName`);
  const networkingTypeValue = getIn(values, networkingTypeFieldName);
  const destinationProviderValue = getIn(values, destinationProviderFieldName);
  const clusterProviderValue = cluster?.inputs?.[CLUSTER_PROVIDER_BACKEND_NAME];

  const { showDestinationProvider, showDestinationRegion } = getDestinationDropdownsVisibility({
    connectorName: connectorNameValue,
    networkingType: networkingTypeValue,
    destinationProvider: destinationProviderValue,
    clusterProvider: clusterProviderValue,
  });

  const connectorNetworkingTypeOptionsConfig = CLUSTER_DETAILS_CONFIG_MAP.get(
    CONNECTOR_AVAILABLE_NETWORKING_TYPES_LIST_BACKEND_NAME
  );
  const networkingTypeDropdownConfig = {
    fieldName: networkingTypeFieldName,
    fieldDisplayName: connectorNetworkingTypeOptionsConfig.displayName,
    dropdownOptions: getConnectorNetworkingOptions(
      connectorNameToRecordsMap,
      connectorNameValue,
      values,
      connectorNetworkingTypeOptionsConfig
    ),
    // Changing the networking type can hide both destination fields, so clear them when it changes.
    ...(IS_CONNECT_CROSS_CLOUD_REGION_LIVE && {
      onChange: () => {
        setFieldValue(destinationProviderFieldName, null);
        setFieldValue(destinationRegionFieldName, null);
      },
    }),
  };

  const smtTypeFieldName = `${connectorPath}.connectorSmtType`;
  const connectorSmtTypeOptionsConfig = CLUSTER_DETAILS_CONFIG_MAP.get(
    CONNECTOR_SMT_TYPE_BACKEND_NAME
  );
  const smtTypeDropdownConfig = {
    fieldName: smtTypeFieldName,
    fieldDisplayName: connectorSmtTypeOptionsConfig?.displayName,
    dropdownOptions: getConnectorSmtTypeOptions(
      connectorNameToRecordsMap,
      connectorNameValue,
      values,
      connectorSmtTypeOptionsConfig
    ),
  };

  const destinationProviderDropdownConfig = {
    fieldName: destinationProviderFieldName,
    fieldDisplayName: CONNECTOR_DESTINATION_PROVIDER_DISPLAY_NAME,
    dropdownOptions: getClusterProviderDropdownOptions(
      cluster?.cluster_configs?.cluster_params,
      values
    ),
    // The region depends on the provider, so clear it whenever the provider changes.
    onChange: () => {
      setFieldValue(destinationRegionFieldName, null);
    },
  };

  const regionOptions = getRegionDropdownOptions(
    cluster?.cluster_configs?.region_details,
    destinationProviderValue
  );
  const filteredDestinationRegionOptions =
    useRegionOptionsWithGovCloudDisabledIfRequired(regionOptions);

  const destinationRegionDropdownConfig = {
    fieldName: destinationRegionFieldName,
    fieldDisplayName: CONNECTOR_DESTINATION_REGION_DISPLAY_NAME,
    dropdownOptions: filteredDestinationRegionOptions,
  };

  const dropdownConfigs = [
    networkingTypeDropdownConfig,
    smtTypeDropdownConfig,
    ...(showDestinationProvider ? [destinationProviderDropdownConfig] : []),
    ...(showDestinationRegion ? [destinationRegionDropdownConfig] : []),
  ];

  return (
    <Grid>
      <Grid.Row>
        {dropdownConfigs.map((dropdownConfig) => (
          <Grid.Column key={dropdownConfig.fieldName} width={3}>
            <ConnectorMetadataDropdownContainer {...dropdownConfig} />
          </Grid.Column>
        ))}
      </Grid.Row>
    </Grid>
  );
};
